import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { SiteFrame, ButtonLink } from '../marketing/components/Shell';
import { NetlifyForm, validators, type FieldDef } from '../marketing/components/Form';
import { Faq } from '../marketing/sections/Closing';
import type { MarketingPage } from '../marketing/routes';
import { splitBlocks, useContent, type LegalDoc } from '../marketing/siteContent';
import '../marketing/site.css';

/*
 * Privacy, terms and support pages. Their text is managed from the admin dashboard
 * (Website → Legal pages). Sections flagged "draft" are visibly marked as pending legal approval.
 */

type Lang = 'ar' | 'en';

/** Arabic by default (matches the pre-rendered page); English via ?lang=en or the app's saved preference. */
function useLegalLanguage(): [Lang, (next: Lang) => void] {
  const [lang, setLang] = useState<Lang>('ar');
  useEffect(() => {
    try {
      const query = new URLSearchParams(window.location.search).get('lang');
      if (query === 'en' || (!query && localStorage.getItem('mealnex-language') === 'en')) setLang('en');
    } catch { /* storage unavailable */ }
  }, []);
  const choose = (next: Lang) => { setLang(next); try { localStorage.setItem('mealnex-language', next); } catch { /* ignore */ } };
  return [lang, choose];
}

/** Renders paragraphs and "- " lists; e-mail addresses become mailto links. */
function RichText({ text }: { text: string }) {
  const linkify = (value: string) => value.split(/([\w.+-]+@[\w-]+\.[\w.-]+)/g).map((part, index) => /@/.test(part) && index % 2 ? <a key={index} href={`mailto:${part}`} dir="ltr">{part}</a> : <Fragment key={index}>{part}</Fragment>);
  return <>{splitBlocks(text).map((block, index) => 'list' in block ? <ul key={index}>{block.list?.map((item, i) => <li key={i}>{linkify(item)}</li>)}</ul> : <p key={index}>{linkify(block.text ?? '')}</p>)}</>;
}

function LegalLayout({ page, titleAr, titleEn, subtitleAr, subtitleEn, children }: { page: MarketingPage; titleAr: string; titleEn: string; subtitleAr: string; subtitleEn: string; children: (lang: Lang) => ReactNode }) {
  const [lang, setLang] = useLegalLanguage();
  const t = (ar: string, en: string) => (lang === 'en' ? en || ar : ar);
  return <SiteFrame page={page}>
    <div className="mx-page mx-container mx-legal">
      <article className="mx-legal-sheet" lang={lang} dir={lang === 'en' ? 'ltr' : 'rtl'}>
        <header className="mx-legal-head">
          <small>{t(subtitleAr, subtitleEn)}</small>
          <h1 className="mx-h1">{t(titleAr, titleEn)}</h1>
          <div className="mx-lang" role="group" aria-label="Language / اللغة">
            <button type="button" aria-pressed={lang === 'ar'} onClick={() => setLang('ar')} lang="ar">العربية</button>
            <button type="button" aria-pressed={lang === 'en'} onClick={() => setLang('en')} lang="en">English</button>
          </div>
        </header>
        {children(lang)}
      </article>
      <nav className="mx-legal-links" aria-label={t('صفحات قانونية', 'Legal pages')}>
        <a href="/privacy" aria-current={page === 'privacy' ? 'page' : undefined}>{t('سياسة الخصوصية', 'Privacy Policy')}</a>
        <a href="/terms" aria-current={page === 'terms' ? 'page' : undefined}>{t('الشروط والأحكام', 'Terms of Use')}</a>
        <a href="/support" aria-current={page === 'support' ? 'page' : undefined}>{t('الدعم', 'Support')}</a>
      </nav>
    </div>
  </SiteFrame>;
}

function LegalDocument({ page, doc }: { page: MarketingPage; doc: LegalDoc }) {
  return <LegalLayout page={page} titleAr={doc.titleAr} titleEn={doc.titleEn} subtitleAr={doc.updatedAr} subtitleEn={doc.updatedEn}>{lang => <>
    <div className="mx-legal-intro"><RichText text={lang === 'en' ? doc.introEn || doc.introAr : doc.introAr} /></div>
    {doc.sections.map((section, index) => <section key={section.id || index} id={section.id || undefined} className={section.draft ? 'is-draft' : undefined}>
      <h2>{lang === 'en' ? section.titleEn || section.titleAr : section.titleAr}{section.draft && <span className="mx-draft-tag">{lang === 'en' ? 'Draft pending legal approval' : 'مسودة بانتظار الاعتماد القانوني'}</span>}</h2>
      <RichText text={lang === 'en' ? section.bodyEn || section.bodyAr : section.bodyAr} />
    </section>)}
  </>}</LegalLayout>;
}

export function PrivacyPolicyPage() {
  return <LegalDocument page="privacy" doc={useContent().legal.privacy} />;
}

export function TermsPage() {
  return <LegalDocument page="terms" doc={useContent().legal.terms} />;
}

const supportFields: FieldDef[] = [
  { name: 'name', label: 'الاسم', required: true, autoComplete: 'name' },
  { name: 'contact', label: 'رقم الجوال أو البريد الإلكتروني', required: true, autoComplete: 'tel', validate: validators.contact, hint: 'لا ترسل رمز التحقق أبدًا' },
  { name: 'type', label: 'أنا', type: 'select', required: true, options: ['صاحب مطعم أو موظف', 'مشترك في أحد المطاعم', 'مهتم بميلنكس'] },
  { name: 'restaurant', label: 'اسم المطعم', optional: true },
  { name: 'message', label: 'كيف نساعدك؟', type: 'textarea', required: true, placeholder: 'صف المشكلة باختصار', maxLength: 1500 },
];

export function SupportPage() {
  const { legal, settings } = useContent();
  const support = legal.support;
  const email = settings.supportEmail;
  return <LegalLayout page="support" titleAr={support.titleAr} titleEn={support.titleEn} subtitleAr={support.updatedAr} subtitleEn={support.updatedEn}>{lang => { const t = (ar: string, en: string) => (lang === 'en' ? en || ar : ar); return <>
    <div className="mx-legal-intro"><RichText text={t(support.introAr, support.introEn)} /></div>
    <div className="mx-support-channels">
      <a className="mx-support-mail" href={`mailto:${email}?subject=Mealnex%20Support`}><span>{t(support.mailLabelAr, support.mailLabelEn)}</span><b dir="ltr">{email}</b></a>
      {settings.phone && <a className="mx-support-mail" href={`tel:${settings.phone.replace(/\s/g, '')}`}><span>{t('اتصل بنا', 'Call us')}</span><b dir="ltr">{settings.phone}</b></a>}
      {settings.appStoreUrl && <a className="mx-support-mail" href={settings.appStoreUrl} target="_blank" rel="noopener"><span>{t('تطبيق المشتركين على iPhone', 'Subscriber app for iPhone')}</span><b dir="ltr">App Store</b></a>}
      {settings.whatsapp && <a className="mx-support-mail" href={`https://wa.me/${settings.whatsapp.replace(/[^\d]/g, '').replace(/^0/, '966')}`} target="_blank" rel="noopener"><span>{t('واتساب', 'WhatsApp')}</span><b dir="ltr">{settings.whatsapp}</b></a>}
    </div>
    <section id="request">
      <h2>{t('أرسل طلب مساعدة', 'Send a help request')}</h2>
      <div lang="ar" dir="rtl"><NetlifyForm name="support" fields={supportFields} submitLabel="أرسل الطلب"
        privacyNote={<>نستخدم هذه البيانات للرد على طلبك فقط، <a href="/privacy">سياسة الخصوصية</a></>}
        success={<><h3 className="mx-h3">وصل طلبك</h3><p className="mx-body">سيرد فريق الدعم عليك عبر وسيلة التواصل التي أدخلتها</p><ButtonLink href="/" variant="quiet">العودة إلى الصفحة الرئيسية</ButtonLink></>} /></div>
    </section>
    {support.quick.length > 0 && <section><h2>{t('أسئلة سريعة', 'Quick help')}</h2>{support.quick.map((item, index) => <Fragment key={index}><h3>{t(item.qAr, item.qEn)}</h3><RichText text={t(item.aAr, item.aEn)} /></Fragment>)}</section>}
    <section><h2>{t('أسئلة أصحاب المطاعم', 'Questions from restaurants')}</h2><div lang="ar" dir="rtl"><Faq id="support-faq" heading={false} /></div></section>
  </>; }}</LegalLayout>;
}
